import React, { useState, useEffect, useRef } from 'react';
import { impersonationManager } from '../../business/auth/ImpersonationManager';
import { accountSessionManager } from '../../business/auth/AccountSessionManager';
import { useAppConfig } from '../../contexts/AppConfigContext';

const ImpersonationBanner: React.FC = () => {
  const { config } = useAppConfig();
  const [timeRemaining, setTimeRemaining] = useState<string>('');
  const bannerRef = useRef<HTMLDivElement | null>(null);

  // Two ways a session can be supervised. The impersonation token lives in localStorage on the
  // origin where impersonation started; a switched session carries the impersonator as a token
  // claim instead, because localStorage does not cross the subdomain hop. Either one must show the
  // banner, or staff lose the cue that they are acting as someone else.
  const isImpersonating =
    impersonationManager.isImpersonating() || accountSessionManager.isImpersonated();
  const originalUser = impersonationManager.getOriginalUser();
  const switchedOperator = accountSessionManager.getImpersonatorName();

  // Countdown timer and auto-revert on token expiry
  useEffect(() => {
    if (!isImpersonating) return;

    const updateTimer = () => {
      if (impersonationManager.isTokenExpired()) {
        impersonationManager.stopImpersonation();
        return;
      }

      const expiry = impersonationManager.getTokenExpiry();
      if (expiry) {
        const diff = expiry.getTime() - Date.now();
        const minutes = Math.floor(diff / 60000);
        const seconds = Math.floor((diff % 60000) / 1000);
        setTimeRemaining(`${minutes}m ${seconds.toString().padStart(2, '0')}s`);
      }
    };

    updateTimer();
    const interval = setInterval(updateTimer, 30000);
    return () => clearInterval(interval);
  }, [isImpersonating]);

  // Publish impersonation state + measured banner height to the document so
  // global CSS can push viewport-fixed overlays (modals, drawers) below the
  // banner without each modal needing to know it exists.
  useEffect(() => {
    const root = document.documentElement;
    if (!isImpersonating) {
      root.removeAttribute('data-impersonating');
      root.style.removeProperty('--app-banner-height');
      return;
    }

    root.dataset.impersonating = 'true';
    const setHeight = () => {
      const h = bannerRef.current?.offsetHeight ?? 0;
      root.style.setProperty('--app-banner-height', `${h}px`);
    };
    setHeight();

    const ro = typeof ResizeObserver !== 'undefined' && bannerRef.current
      ? new ResizeObserver(setHeight)
      : null;
    if (ro && bannerRef.current) ro.observe(bannerRef.current);

    return () => {
      ro?.disconnect();
      root.removeAttribute('data-impersonating');
      root.style.removeProperty('--app-banner-height');
    };
  }, [isImpersonating]);

  if (!isImpersonating) return null;

  const impersonatedName = config?.currentUser?.name || config?.currentUser?.userName || 'Unknown User';

  return (
    <div ref={bannerRef} className="bg-amber-500 text-white px-4 py-2 flex items-center justify-between text-sm flex-none z-[60]">
      <div className="flex items-center gap-4">
        <span className="font-semibold">
          Viewing as: {impersonatedName}
        </span>
        {(originalUser?.name || switchedOperator) && (
          <span className="opacity-80">
            Logged in as {originalUser?.name || switchedOperator}
          </span>
        )}
        {timeRemaining && (
          <span className="opacity-80">
            Expires in {timeRemaining}
          </span>
        )}
      </div>
      <button
        onClick={() =>
          // In a switched session the impersonation token lives on the origin we came FROM, so
          // stopping means leaving the switched session — which returns there and resumes it.
          accountSessionManager.isActive()
            ? accountSessionManager.exit()
            : impersonationManager.stopImpersonation()
        }
        className="bg-surface text-amber-700 px-3 py-1 rounded text-sm font-medium hover:bg-amber-50 transition-colors"
      >
        Stop Impersonation
      </button>
    </div>
  );
};

export default ImpersonationBanner;
